import React from 'react';
import { pageMarginBoxes } from '../../../utils/printDocument';

// The letterhead print view. Hidden on screen; window.print() makes it the only visible thing,
// using the same visibility mechanism as the invoice print (only one printable block can exist
// on screen at a time, which holds because only one report renders at a time).
//
// Inline styles throughout: Tailwind classes are not guaranteed to resolve inside the print
// context. Charts are deliberately not printed - the tables are the record; the charts are for
// the screen.
//
// Props:
//   title    - report name
//   subtitle - period / filters line
//   kpis     - [{ label, value }] headline figures
//   tables   - [{ title, subtitle?, columns: [{ key, label, align?, nowrap? }], rows, emptyText? }]
//   fields   - optional [{ label, value }] facts printed under the title (a drill's own details)
//   footnote - optional sentence printed under the tables
//   signatures - optional labels for sign-off lines under the tables ("Prepared by", ...), for a
//                copy that is signed and passed on
//
// A report prints this through window.print() over the page; a report's detail window prints it in
// a clean window instead (printReport.js), where none of the app's styles reach it.

const cellStyle = (align, nowrap) => ({
  padding: '4px 8px',
  borderBottom: '1px solid #ddd',
  textAlign: align === 'right' ? 'right' : 'left',
  fontSize: '11px',
  ...(nowrap ? { whiteSpace: 'nowrap' } : {}),
});

const FIELDS_PER_ROW = 4;

const inRows = (list, n) => {
  const out = [];
  for (let i = 0; i < list.length; i += n) out.push(list.slice(i, i + n));
  return out;
};

const ReportPrintLayout = ({ title, subtitle, kpis = [], tables = [], fields = [], footnote, signatures = [] }) => (
  <div className="hidden print:block" data-report-print={title} style={{ fontFamily: 'Arial, Helvetica, sans-serif', color: '#111' }}>
    <style>{`
      @media print {
        body * { visibility: hidden; }
        .print\\:block, .print\\:block * { visibility: visible; }
        .print\\:block { position: absolute; left: 0; top: 0; width: 100%; }
        @page { margin: 12mm; ${pageMarginBoxes()} }
      }
    `}</style>

    {/* Letterhead */}
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #003078', paddingBottom: '8px' }}>
      <div>
        <div style={{ fontSize: '18px', fontWeight: 'bold' }}>Perveen Aslam Memorial Hospital</div>
        <div style={{ fontSize: '11px', color: '#555' }}>PAMH Management Reports</div>
      </div>
      <div style={{ fontFamily: "'Noto Nastaliq Urdu', serif", direction: 'rtl', fontSize: '15px', paddingTop: '2px' }}>
        پروین اسلم میموریل ہسپتال
      </div>
    </div>

    <div style={{ marginTop: '10px' }}>
      <div style={{ fontSize: '15px', fontWeight: 'bold' }}>{title}</div>
      <div style={{ fontSize: '11px', color: '#555' }}>
        {subtitle} · Printed {new Date().toLocaleString('en-PK')}
      </div>
    </div>

    {fields.length > 0 && (
      <table style={{ width: '100%', marginTop: '8px', borderCollapse: 'collapse' }}>
        <tbody>
          {inRows(fields, FIELDS_PER_ROW).map((row, ri) => (
            <tr key={ri}>
              {row.map((f, i) => (
                <td key={i} style={{ padding: '3px 12px 3px 0', verticalAlign: 'top', width: `${100 / FIELDS_PER_ROW}%` }}>
                  <div style={{ fontSize: '9px', color: '#666', textTransform: 'uppercase' }}>{f.label}</div>
                  <div style={{ fontSize: '11px' }}>{f.value ?? '—'}</div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    )}

    {kpis.length > 0 && (
      <table style={{ width: '100%', marginTop: '10px', borderCollapse: 'collapse' }}>
        <tbody>
          <tr>
            {kpis.map((k, i) => (
              <td key={i} style={{ border: '1px solid #ddd', padding: '6px 8px' }}>
                <div style={{ fontSize: '9px', color: '#666', textTransform: 'uppercase' }}>{k.label}</div>
                <div style={{ fontSize: '13px', fontWeight: 'bold' }}>{k.value ?? '—'}</div>
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    )}

    {tables.map((t, ti) => (
      <div key={ti} style={{ marginTop: '14px' }}>
        <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>
          {t.title}
          {t.subtitle && <span style={{ fontWeight: 'normal', color: '#555' }}> · {t.subtitle}</span>}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {t.columns.map((c) => (
                <th key={c.key} style={{ ...cellStyle(c.align), background: '#f3f4f6', fontWeight: 'bold', borderBottom: '1px solid #999' }}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {t.rows.length === 0 ? (
              <tr><td colSpan={t.columns.length} style={{ ...cellStyle(), color: '#888' }}>{t.emptyText || 'No data'}</td></tr>
            ) : (
              t.rows.map((row, ri) => (
                <tr key={ri}>
                  {t.columns.map((c) => (
                    <td key={c.key} style={cellStyle(c.align, c.nowrap)}>
                      {(c.csv ? c.csv(row) : row[c.key]) ?? '—'}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    ))}

    {footnote && (
      <div style={{ marginTop: '10px', fontSize: '10px', color: '#555' }}>{footnote}</div>
    )}

    {signatures.length > 0 && (
      <table style={{ width: '100%', marginTop: '40px', borderCollapse: 'collapse', pageBreakInside: 'avoid' }}>
        <tbody>
          <tr>
            {signatures.map((s) => (
              <td key={s} style={{ padding: '0 14px', width: `${100 / signatures.length}%`, verticalAlign: 'top' }}>
                <div style={{ borderTop: '1px solid #333', paddingTop: '4px', fontSize: '11px', fontWeight: 'bold' }}>{s}</div>
                <div style={{ fontSize: '9px', color: '#666', marginTop: '2px' }}>Name, signature and date</div>
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    )}

    <div style={{ marginTop: '16px', paddingTop: '6px', borderTop: '1px solid #ddd', fontSize: '9px', color: '#888' }}>
      Perveen Aslam Memorial Hospital · PAMH System
    </div>
  </div>
);

export default ReportPrintLayout;
